import { FunctionComponent, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { DeleteFlightModal } from "./DeleteFlightModal";

type Props = {
  items: Flight[];
  showOperator?: boolean;
  canDelete?: boolean;
};

type FlightDayGroup = {
  key: string;
  label: string;
  flights: Flight[];
};

const dateHeaderFormatter = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "2-digit",
  timeZone: "UTC",
});

const timeFormatter = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

const uploadDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const getFlightDateKey = (flight: Flight) => {
  const value = flight.scheduled_time_of_departure;
  if (!value) {
    return "unknown";
  }

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) {
    return "unknown";
  }

  return date.toISOString().slice(0, 10);
};

export const formatFlightTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) {
    return "-";
  }

  return `${timeFormatter.format(date)}Z`;
};

export const formatFlightUploadedAt = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "—";
  return `${uploadDateFormatter.format(date)} · ${timeFormatter.format(date)}Z`;
};

export const formatFlightDayHeader = (key: string) => {
  if (key === "unknown") {
    return "Date unknown";
  }

  const date = new Date(`${key}T00:00:00Z`);
  if (Number.isNaN(date.valueOf())) {
    return "Date unknown";
  }

  return dateHeaderFormatter.format(date);
};

export const resolveFlightNumber = (flight: Flight) => {
  const identifier = `${flight.flight_identifier || ""}`.trim();
  return identifier || flight.flightID || "-";
};

export const resolveDiversionAirport = (flight: Flight) => {
  const isDiverted =
    flight?.is_diverted === true ||
    flight?.is_diverted === 1 ||
    flight?.is_diverted === "1";

  if (!isDiverted) {
    return null;
  }

  const airport = `${flight.diverted_to_airport || flight.actual_arrival_airport || ""}`
    .trim()
    .toUpperCase();

  return airport || null;
};

export const groupFlightsByDay = (items: Flight[]): FlightDayGroup[] => {
  const groups = new Map<string, Flight[]>();

  [...items]
    .sort((a, b) =>
      `${b.scheduled_time_of_departure || ""}`.localeCompare(
        `${a.scheduled_time_of_departure || ""}`,
      ),
    )
    .forEach((flight) => {
      const key = getFlightDateKey(flight);
      const entries = groups.get(key) || [];
      entries.push(flight);
      groups.set(key, entries);
    });

  return Array.from(groups.entries()).map(([key, flights]) => ({
    key,
    label: formatFlightDayHeader(key),
    flights: flights.sort((a, b) =>
      `${b.scheduled_time_of_departure || ""}`.localeCompare(
        `${a.scheduled_time_of_departure || ""}`,
      ),
    ),
  }));
};

export const FlightsList: FunctionComponent<Props> = ({
  items,
  showOperator = false,
  canDelete = true,
}) => {
  const [deleteID, setDeleteId] = useState("");
  const [actionMenu, setActionMenu] = useState<{ anchor: HTMLElement; flight: Flight } | null>(null);
  const navigate = useNavigate();
  const groups = useMemo(() => groupFlightsByDay(items || []), [items]);

  if (!items?.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          textAlign: "center",
        }}
      >
        <Typography color="text.secondary">No flights found</Typography>
      </Paper>
    );
  }

  const handleCloseModal = () => setDeleteId("");

  const handleMenuAction = (path: string) => {
    setActionMenu(null);
    navigate(path);
  };

  return (
    <Stack spacing={2}>
      <DeleteFlightModal id={deleteID} onClose={handleCloseModal} />
      <Menu anchorEl={actionMenu?.anchor || null} open={Boolean(actionMenu)} onClose={() => setActionMenu(null)}>
        {actionMenu ? [
          <MenuItem key="open" onClick={() => handleMenuAction(`/flight/${actionMenu.flight.flightID}`)}>Open flight</MenuItem>,
          <MenuItem key="journey" onClick={() => handleMenuAction(`/flight/${actionMenu.flight.flightID}?action=journey-log`)}>Journey log</MenuItem>,
          <MenuItem key="send" onClick={() => handleMenuAction(`/flight/${actionMenu.flight.flightID}?action=send-files`)}>Send files</MenuItem>,
          canDelete ? <MenuItem key="delete" sx={{ color: "error.main" }} onClick={() => { setDeleteId(actionMenu.flight.flightID); setActionMenu(null); }}>Delete flight…</MenuItem> : null,
        ] : null}
      </Menu>
      {groups.map((group) => (
        <Paper
          key={group.key}
          elevation={0}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: (theme) => theme.palette.action.hover,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {group.label}
              </Typography>
              <Chip
                size="small"
                label={`${group.flights.length} ${group.flights.length === 1 ? "flight" : "flights"}`}
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Box>
          <TableContainer>
            <Table size="small" aria-label={`Flights for ${group.label}`}>
              <TableHead>
                <TableRow
                  sx={(theme) => ({
                    "& th": {
                      color: theme.palette.text.secondary,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    },
                  })}
                >
                  <TableCell>Time</TableCell>
                  <TableCell>Commercial number</TableCell>
                  <TableCell>Flight</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>TLC</TableCell>
                  <TableCell>Uploaded</TableCell>
                  {showOperator ? <TableCell>Operator</TableCell> : null}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.flights.map((flight) => {
                  const diversionAirport = resolveDiversionAirport(flight);

                  return (
                    <TableRow
                      key={flight.flightID}
                      hover
                      tabIndex={0}
                      data-flight-list-row="true"
                      onClick={() => navigate(`/flight/${flight.flightID}`)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          navigate(`/flight/${flight.flightID}`);
                        }
                      }}
                      sx={{
                        "& td": { verticalAlign: "middle" },
                        cursor: "pointer",
                      }}
                    >
                    <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                      {formatFlightTime(flight.scheduled_time_of_departure)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {flight.commercialFlightNumber || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography
                        component={RouterLink}
                        to={`/flight/${flight.flightID}`}
                        onClick={(event) => event.stopPropagation()}
                        variant="subtitle2"
                        sx={{
                          color: "text.primary",
                          fontWeight: 800,
                          textDecoration: "none",
                          "&:hover": {
                            color: "primary.main",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {resolveFlightNumber(flight)}
                        {flight.is_ramp_return ? " · Ramp return" : ""}
                      </Typography>
                      {flight.relatedFlights?.map(related => <Typography key={related.flightId} component={RouterLink} to={`/flight/${related.flightId}`} onClick={event=>event.stopPropagation()} onKeyDown={event=>event.stopPropagation()} variant="caption" sx={{display:"block"}}>{related.isRampReturn ? "Ramp return" : "Original flight"} #{related.flightId}</Typography>)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {flight.departure_airport || "-"} → {flight.arrival_airport || "-"}
                        </Typography>
                        {diversionAirport ? (
                          <Chip
                            color="error"
                            label={`diverted to ${diversionAirport}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                            }}
                          />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {flight.aircraft_registration || "-"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {flight.user_tlc || "-"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "var(--font-mono)" }}>
                        {formatFlightUploadedAt(flight.uploadedAt)}
                      </Typography>
                    </TableCell>
                    {showOperator ? (
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {flight.operator_name || flight.operator_ref || "-"}
                      </TableCell>
                    ) : null}
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Button
                        component={RouterLink}
                        to={`/flight/${flight.flightID}`}
                        data-list-primary-action="true"
                        size="small"
                        onClick={(event) => event.stopPropagation()}
                      >Open</Button>
                      <Tooltip title="More flight actions">
                        <IconButton
                          size="small"
                          aria-label={`More actions for flight ${flight.flightID}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setActionMenu({ anchor: event.currentTarget, flight });
                          }}
                        >
                          <MoreVertIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Stack>
  );
};
